import React, { useState, FC, ReactElement, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { Grid, TextField, Typography, Box } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import MicNoneIcon from "@mui/icons-material/MicNone";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxhook";
import {
  addConversations,
  appendChat,
  getChats,
  getSelectedIndexes,
  getconversationid,
  // limitChatHistory,
} from "../../redux/slices/conversationSlice";
import { getType, getUserFormatPrompt } from "../../redux/slices/typeSlice";
import { getModelDetails } from "../../redux/slices/modelSlice";
import {
  getPIIInfo,
  getSendingFullDocument,
  getuser,
  getChatHistory,
} from "../../redux/slices/userSlice";
import { addError } from "../../redux/slices/errorSlice";
import makeHistory from "../../utils/makeHistory";
import apiCall from "../../utils/apiCall";
import Lottie from "lottie-react";
import animationData from "../../assets/typing.json";
import {
  FIRST_MESSAGE_CHANGE_CONVERSATION_NAME,
  // CHAT_WITH_IMAGE_GENERATOR,
} from "../../endpoint";
import { loadingforconversation } from "../../redux/slices/loadingSlice";
import { getcollection } from "../../redux/slices/collectionSlice";
import { COLLECTION, WEB, VANILLA, PDF, IMAGE } from "../../data/constants";
import useSpeechToText from "../../hooks/azuretest";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material";

// import a small icon image for the left of textfield
// adjust the path if your asset is located elsewhere
import chatIcon from "../../assets/Ally-Avatar.png";

const ChatInput: FC<{ onScroll: () => void }> = ({
  onScroll,
}): ReactElement => {
  const [typing, setTyping] = useState(false);
  const [switchTimer, setSwitchTimer] = useState(0);

  const chats = useAppSelector(getChats);
  const chatType = useAppSelector(getType);
  const selectedIndexes = useAppSelector(getSelectedIndexes);
  const current_conversation_id = useAppSelector(getconversationid);
  const user = useAppSelector(getuser);
  const model = useAppSelector(getModelDetails);
  const prompt = useAppSelector(getUserFormatPrompt);
  const collection = useAppSelector(getcollection);
  const dispatch = useAppDispatch();
  const full_doc = useAppSelector(getSendingFullDocument);
  const pii = useAppSelector(getPIIInfo);
  const chatHistoryLimit = useAppSelector(getChatHistory);

  const {
    transcript,
    isListening,
    startListening,
    stopListening,
    setTranscript,
    error,
    setError,
  } = useSpeechToText();

  const { accounts } = useMsal();
  const account = accounts[0];

  const data = [
    "Extracting relevant information from sources",
    "LLM getting your response ready",
  ];

  // ... generateImage, sendMessage, typingHandler, useEffect timers etc ...
  // keep your existing implementations (omitted here for brevity in this snippet)
  // I'll include sendMessage and typingHandler directly so the file is complete:

  const generateImage = async (query: string) => {
    try {
      const chat_hist = makeHistory([...chats], user.chat_history);
      const response = await apiCall("POST", chatType.chaturl, {
        query,
        chat_hist,
        model: model.model,
        temp: 0,
        conv_id: current_conversation_id,
        pii: pii ? true : false,
      });
      return response;
    } catch (error) {
      dispatch(addError("Failed to generate image. Please try again."));
      throw error;
    }
  };

  const sendMessage = async (event: any, func = false) => {
    if (
      (event.key === "Enter" && transcript.trim()) ||
      (func && transcript.trim())
    ) {
      if (chatType.geturl !== "" && selectedIndexes.length === 0) {
        const errorMessage =
          chatType.type === PDF
            ? "Please upload and select a document"
            : chatType.type === IMAGE
            ? "Please upload and select an image."
            : chatType.type === COLLECTION
            ? "Please create and select a collection"
            : "Please select an index";
        dispatch(addError(errorMessage));
      } else {
        const message = transcript;
        setTranscript("");
        const chat_hist = makeHistory([...chats], user.chat_history);
        setTyping(true);
        dispatch(
          appendChat({
            message,
            type: "user",
          })
        );
        onScroll();

        apiCall("POST", chatType.chaturl, {
          query: message,
          // chat_hist: chat_hist,
          chat_hist:
            chatType.type === WEB || chatType.type === VANILLA ? chat_hist : [],
          model:
            chatType.type === WEB
              ? window.__RUNTIME_CONFIG__?.REACT_APP_WEB_MODEL ||
                process.env.REACT_APP_WEB_MODEL ||
                model.model
              : model.model,
          temp: "0",
          chunks:
            chatType.type === COLLECTION ? collection.files : selectedIndexes,
          chunk_size: user.chunk_size,
          search_type: "similarity",
          conv_id: current_conversation_id,
          user_email: account?.username || "",
          images:
            chatType.type === COLLECTION ? collection.images : selectedIndexes,
          video_names: selectedIndexes,
          name: selectedIndexes[0] || "",
          prompt: prompt || "",
          file_name: selectedIndexes[0] || "",
          db_name: selectedIndexes[0] || "",
          tables: ["all"],
          llm_doc: model.model || "",
          llm_img: model.imagemodel || "",
          db_call: false,
          full_doc,
          pii: pii ? true : false,
          web_search: chatType.type === WEB,
        })
          .then(({ result, sources, cypher, org_cypher, creds }) => {
            if (chats.length === 0) {
              dispatch(loadingforconversation(true));
              apiCall("POST", FIRST_MESSAGE_CHANGE_CONVERSATION_NAME, {
                user_email: account.username,
                conv_id: current_conversation_id,
                question: message,
                answer: result?.result,
              })
                .then((res) => {
                  dispatch(addConversations(res?.result));
                })
                .catch((err) => {
                  dispatch(
                    addError(
                      "Failed to load the conversations name. Please try again."
                    )
                  );
                })
                .finally(() => {
                  dispatch(loadingforconversation(false));
                });
            }
            dispatch(
              appendChat({
                type: "gpt",
                message: result,
                sources,
                model: model.model,
                chat_type: chatType.shortname,
                cypher,
                org_cypher,
                db_creds: creds,
                image_model:
                  chatType.type === COLLECTION ? model.imagemodel : "",
                collection_chunks: collection?.files,
                collection_images: collection?.images,
                pii,
                web_search: chatType.type === WEB,
              })
            );

            // if (chatHistoryLimit > 0) {
            //   dispatch(limitChatHistory(chatHistoryLimit));
            // }

            setTyping(false);
            onScroll();
          })
          .catch((err) => {
            console.error("API Error:", err);
            setTyping(false);
            const errorMessage =
              err?.detail ||
              err?.message ||
              "An error occurred while processing your request";
            dispatch(
              appendChat({
                type: "gpt",
                message: errorMessage,
              })
            );

            // if (chatHistoryLimit > 0) {
            //   dispatch(limitChatHistory(chatHistoryLimit));
            // }
          })
          .finally(() => {
            onScroll();
          });
      }
    }
  };

  const typingHandler = (e: any) => {
    setTranscript(e.target.value);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setSwitchTimer((timer) => (timer === 0 ? 1 : 0));
    }, 2000);
    return () => {
      clearInterval(timer);
    };
  }, []);

  const theme = useTheme();
  return (
    <Box
      sx={{
        width: "100%",
        mt: 3,
        mb: 1,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden", // Prevent horizontal scrolling
      }}
    >
      {typing && (
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Lottie
            animationData={animationData}
            style={{ width: 60, height: 60 }}
          />
          <Typography fontSize="small" sx={{ color: "gray", ml: 1 }}>
            {data[switchTimer]}
          </Typography>
        </Box>
      )}
      {error && (
        <Box
          sx={{
            background: "#ffd4cc",
            px: 1,
            mb: 0.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontSize: "12px" }} color="error">
            {error}
          </Typography>
          <CloseIcon
            sx={{ fontSize: "12px", color: "#f44336", cursor: "pointer" }}
            onClick={() => setError(undefined)}
          />
        </Box>
      )}
      {isListening && (
        <Box
          sx={{
            px: 1,
            mb: 0.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontSize: "12px" }} color="primary">
            Recording... Click on the pause button to stop recording
          </Typography>
        </Box>
      )}

      {/* Row: icon, textfield, actions */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* circular wrapper around the icon */}
        <Box
          sx={{
            // ml: 2,            // spacing from left
            mr: 1.5, // space between circle and textfield
            width: 35, // circle width
            height: 35, // circle height
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            // pl: "20px",
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(252, 252, 252, 1)"
                : "rgba(255, 255, 255, 1)", // subtle bg that adapts to theme
            cursor: "pointer", // makes it feel clickable (optional)
            userSelect: "none",
            boxShadow:
              theme.palette.mode === "dark"
                ? "none"
                : "0 1px 2px rgba(0,0,0,0.04)",
          }}
          onClick={() => {
            /* optional handler: open profile or any action */
          }}
          aria-label="chat icon"
        >
          <Box
            component="img"
            src={chatIcon}
            alt="chat icon"
            sx={{
              height: 22, // icon size inside the circle
              width: "auto",
              display: "block",
              pointerEvents: "none", // allow wrapper click
            }}
          />
        </Box>

        {/* text field */}
        <Box sx={{ flex: 1, display: "flex" }}>
          <TextField
            id="outlined-multiline-flexible"
            multiline
            maxRows={8}
            minRows={1}
            className="chat_input"
            placeholder="Ask me anything . . ."
            value={transcript}
            onChange={typingHandler}
            autoFocus
            disabled={typing || chatType.type === ""}
            hiddenLabel
            fullWidth
            size="small"
            sx={{
              background:
                theme.palette.mode === "dark" ? "#000000d6" : "#ffffffd6",
              "& .MuiInputBase-input::placeholder": {
                color: theme.palette.mode === "dark" ? "#aaa" : "#333",
                opacity: 1,
              },
            }}
            variant="outlined"
            onKeyDown={(e) => {
              if (e.shiftKey && e.key === "Enter") return;
              if (e.key === "Enter") sendMessage(e);
            }}
          />
        </Box>

        {/* actions */}
        <Box sx={{ ml: 2, display: "flex", alignItems: "center" }}>
          <SendIcon
            color="primary"
            sx={{ fontSize: "25px", mr: 1, cursor: "pointer" }}
            onClick={() => sendMessage("", true)}
          />
          {isListening ? (
            <StopCircleIcon
              onClick={stopListening}
              color="primary"
              sx={{ fontSize: "35px", cursor: "pointer" }}
            />
          ) : (
            <MicNoneIcon
              id="micicon"
              onClick={startListening}
              color="primary"
              sx={{ fontSize: "35px", cursor: "pointer" }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChatInput;
